import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/Login';
import OtpVerify from './pages/OtpVerify';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import Employees from './pages/Employees';
import Licenses from './pages/Licenses';
import Repairs from './pages/Repairs';
import Tickets from './pages/Tickets';
import Damages from './pages/Damages';
import Toner from './pages/Toner';
import Accessories from './pages/Accessories';
import Donations from './pages/Donations';
import Insurance from './pages/Insurance';
import Offboarding from './pages/Offboarding';
import AccessManagement from './pages/AccessManagement';
import InHandAssets from './pages/InHandAssets';
import KPIs from './pages/KPIs';
import MyPortal from './pages/MyPortal';
import MyPolicies from './pages/MyPolicies';
import MasterFiles from './pages/MasterFiles';
import Policies from './pages/Policies';
import Dongles from './pages/Dongles';
import Voice from './pages/Voice';
import Analysis from './pages/Analysis';
import AuditTrail from './pages/AuditTrail';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import AccessoryRequests from './pages/AccessoryRequests';
import MyAccessoryRequests from './pages/MyAccessoryRequests';
import AIInsights from './pages/AIInsights';
import ITBudget from './pages/ITBudget';
import ITRenewals from './pages/ITRenewals';
import ERPAccess from './pages/ERPAccess';
import PolicyAcknowledgementModal from './components/PolicyAcknowledgementModal';
import TicketAcknowledgementModal from './components/TicketAcknowledgementModal';
import api from './api/client';

// Checks for pending policy acknowledgements and shows blocking modal if any
function PolicyGate({ children }) {
  const { user } = useAuth();
  const [pendingAcks, setPendingAcks] = React.useState(null); // null = not loaded yet

  React.useEffect(() => {
    if (!user) { setPendingAcks([]); return; }
    api.get('/policies/my-pending-acks')
      .then(r => setPendingAcks(r.data || []))
      .catch(() => setPendingAcks([]));
  }, [user]);

  if (pendingAcks === null) return null; // Still loading — don't flash anything
  if (pendingAcks.length > 0) {
    return <PolicyAcknowledgementModal pendingAcks={pendingAcks} onDone={() => setPendingAcks([])}/>;
  }
  return children;
}

// Shows unacknowledged ticket modal for IT staff on login
function TicketAcknowledgementGate({ children }) {
  const { user } = useAuth();
  const [unackTickets, setUnackTickets] = React.useState(null);

  React.useEffect(() => {
    if (!user || !['it_staff', 'junior_it'].includes(user.role)) { setUnackTickets([]); return; }
    api.get('/tickets/my-unacknowledged')
      .then(r => setUnackTickets(r.data || []))
      .catch(() => setUnackTickets([]));
  }, [user]);

  return (
    <>
      {children}
      {unackTickets?.length > 0 && (
        <TicketAcknowledgementModal tickets={unackTickets} onDone={() => setUnackTickets([])}/>
      )}
    </>
  );
}

const PrivateRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <PolicyGate><TicketAcknowledgementGate><Layout>{children}</Layout></TicketAcknowledgementGate></PolicyGate>;
};

// General user lands on MyPortal; everyone else gets the requested page (Dashboard)
const GeneralUserRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role === 'general_user') {
    return (
      <PolicyGate>
        <Layout>
          <ErrorBoundary>
            <MyPortal />
          </ErrorBoundary>
        </Layout>
      </PolicyGate>
    );
  }
  return <PolicyGate><Layout>{children}</Layout></PolicyGate>;
};

// Pages only certain roles may open, even by typing the URL. The API enforces
// the same rule — this just avoids showing an empty page that 403s.
const RoleRoute = ({ roles, children }) => {
  const { user } = useAuth();
  if (user && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <PrivateRoute>{children}</PrivateRoute>;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user && !user.must_change_password ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/otp-verify" element={<OtpVerify />} />
      <Route path="/change-password" element={user ? <ChangePassword /> : <Navigate to="/login" replace />} />
      <Route path="/" element={<GeneralUserRoute><Dashboard /></GeneralUserRoute>} />
      <Route path="/my-portal" element={<PrivateRoute><MyPortal /></PrivateRoute>} />
      <Route path="/my-tickets" element={<PrivateRoute><Tickets /></PrivateRoute>} />
      <Route path="/my-policies" element={<PrivateRoute><MyPolicies /></PrivateRoute>} />
      <Route path="/my-assets" element={<PrivateRoute><Assets /></PrivateRoute>} />
      <Route path="/assets" element={<PrivateRoute><Assets /></PrivateRoute>} />
      <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
      <Route path="/licenses" element={<PrivateRoute><Licenses /></PrivateRoute>} />
      <Route path="/repairs" element={<PrivateRoute><Repairs /></PrivateRoute>} />
      <Route path="/tickets" element={<PrivateRoute><Tickets /></PrivateRoute>} />
      <Route path="/damages" element={<PrivateRoute><Damages /></PrivateRoute>} />
      <Route path="/toner" element={<PrivateRoute><Toner /></PrivateRoute>} />
      <Route path="/accessories" element={<PrivateRoute><Accessories /></PrivateRoute>} />
      <Route path="/donations" element={<PrivateRoute><Donations /></PrivateRoute>} />
      <Route path="/insurance" element={<PrivateRoute><Insurance /></PrivateRoute>} />
      <Route path="/offboarding" element={<PrivateRoute><Offboarding /></PrivateRoute>} />
      <Route path="/access" element={<PrivateRoute><AccessManagement /></PrivateRoute>} />
      <Route path="/in-hand" element={<PrivateRoute><InHandAssets /></PrivateRoute>} />
      <Route path="/kpis" element={<PrivateRoute><KPIs /></PrivateRoute>} />
      <Route path="/master-files" element={<PrivateRoute><MasterFiles /></PrivateRoute>} />
      <Route path="/policies" element={<PrivateRoute><Policies /></PrivateRoute>} />
      <Route path="/dongles" element={<PrivateRoute><Dongles /></PrivateRoute>} />
      <Route path="/voice" element={<PrivateRoute><Voice /></PrivateRoute>} />
      <Route path="/analysis" element={<PrivateRoute><Analysis /></PrivateRoute>} />
      <Route path="/audit" element={<PrivateRoute><AuditTrail /></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/accessory-requests" element={<PrivateRoute><AccessoryRequests /></PrivateRoute>} />
      <Route path="/my-accessory-requests" element={<PrivateRoute><MyAccessoryRequests /></PrivateRoute>} />
      <Route path="/ai-insights" element={<PrivateRoute><AIInsights /></PrivateRoute>} />
      <Route path="/budget" element={<RoleRoute roles={['admin']}><ITBudget /></RoleRoute>} />
      <Route path="/renewals" element={<RoleRoute roles={['admin']}><ITRenewals /></RoleRoute>} />
      <Route path="/erp-access" element={<RoleRoute roles={['admin','director']}><ERPAccess /></RoleRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
